import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { exportXlsx } from '~/eds/src/utils/exportXlsx';
import { TableContextType } from '~/enums/TableContextType';
import { Filter } from '~/evifields/src/types/Filter';
import { AuditLogsFilters } from '~/features/audit-logs/AuditLogsFilters';
import { initialFilters } from '~/features/audit-logs/constants';
import { getOptionLabels } from '~/features/audit-logs/getOptionLabels';
import { AuditLogRecord } from '~/features/audit-logs/types';
import { validateFilters } from '~/features/audit-logs/utils';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useTableSettings } from '~/hooks/useTableSettings';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';
import { coerceRtkqError } from '~/redux/api/utils';
import { RoutePathType } from '~/routing/enums';
import { generateNav } from '~/routing/nav';
import { useRouting } from '~/routing/useRouting';

const Page = () => {
  const intl = useIntl();

  const formatDate = useFormatDate();
  const { client } = useCurrentUser();
  const { toast } = useToast();
  const { getSearchParams, setSearchParams } = useRouting();

  const nav = generateNav({
    current: {
      text: intl.formatMessage({
        id: 'AdminConsoleAuditLogs.auditLogs',
        defaultMessage: 'Audit Logs',
        description: 'Navigation breadcrumb text for audit logs page',
      }),
      pathname: RoutePathType.AdminConsoleClientAuditLogs,
    },
    from: RoutePathType.AdminConsoleClient,
    params: {
      clientId: client,
    },
    intl,
  });

  const { tableSettings = initialTableSettings } = useTableSettings(
    TableContextType.AuditLogs,
  );

  const searchParams = getSearchParams();

  const pageIndex = Number(
    searchParams.pageIndex ?? initialTableSettings.pageIndex,
  );

  // Read pageSize from URL params first (so query refetches), fallback to tableSettings
  const pageSize = searchParams.pageSize
    ? Number(searchParams.pageSize)
    : (tableSettings.pageSize ?? initialTableSettings.pageSize);

  const sortBy = parseSortByParam(searchParams.sortBy);

  const persistedFilters = searchParams.filters
    ? parseFiltersParams(searchParams.filters)
    : initialFilters;

  const [filters, setFilters] = useState<Filter[]>(persistedFilters);

  const { entityOptions } = getOptionLabels(intl);

  const {
    data = { results: [], totalCount: 0, hasMore: false },
    isFetching: isLoading,
    error,
  } = api.endpoints.getAuditLogs.useQuery({
    filters,
    pageIndex,
    pageSize,
    sortBy,
    formatDate,
  });

  const [getAuditLogsExport, { isFetching: isLoadingExportRecords }] =
    api.endpoints.getAuditLogs.useLazyQuery();

  const { results, totalCount, hasMore } = data;

  useEffect(() => {
    if (isLoadingExportRecords) {
      toast({
        status: 'info',
        message: intl.formatMessage({
          id: 'AdminConsoleAuditLogs.exportingMessage',
          defaultMessage: 'Exporting audit logs…',
          description: 'Toast message shown while exporting audit logs',
        }),
      });
    }
    if (coerceRtkqError(error)?.response?.data?.data) {
      toast({
        status: 'danger',
        message: `${coerceRtkqError(error).response.data.data}`,
      });
    }
  }, [isLoadingExportRecords, error]);

  const placeholderContent = error
    ? {
        message: intl.formatMessage({
          id: 'AdminConsoleAuditLogs.failedToLoad',
          defaultMessage: 'Failed to load audit logs.',
          description: 'Error message when audit logs fail to load',
        }),
      }
    : totalCount === 0
      ? {
          message: intl.formatMessage({
            id: 'AdminConsoleAuditLogs.noLogsFound',
            defaultMessage: 'No audit logs found.',
            description: 'Message when no audit logs are found',
          }),
        }
      : undefined;

  const loadingContent = {
    isLoading,
    message: intl.formatMessage({
      id: 'AdminConsoleAuditLogs.loadingMessage',
      defaultMessage: 'Loading audit log',
      description: 'Loading message for audit logs',
    }),
  };

  const columns = [
    {
      key: 'datetime',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'AdminConsoleAuditLogs.dateColumn',
        defaultMessage: 'Date',
        description: 'Column header for datetime',
      }),
      mapCellProps: (d: AuditLogRecord) => ({
        datetime: d.datetime,
        format: 'short_datetime',
      }),
      width: 'l',
    },
    {
      key: 'initiatorName',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'AdminConsoleAuditLogs.initiatorNameColumn',
        defaultMessage: 'Initiator Name',
        description: 'Column header for initiator name',
      }),
      mapCellProps: (d: AuditLogRecord) => ({
        text: d.initiatorName,
      }),
    },
    {
      key: 'recordType',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'AdminConsoleAuditLogs.recordTypeColumn',
        defaultMessage: 'Record Type',
        description: 'Column header for record type',
      }),
      mapCellProps: (d: AuditLogRecord) => ({
        text: d.recordType,
      }),
    },
    {
      key: 'recordName',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'AdminConsoleAuditLogs.recordNameColumn',
        defaultMessage: 'Record Name',
        description: 'Column header for record name',
      }),
      mapCellProps: (d: AuditLogRecord) => ({
        text: d.recordName,
      }),
    },
    {
      key: 'activity',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'AdminConsoleAuditLogs.activityColumn',
        defaultMessage: 'Activity',
        description: 'Column header for activity',
      }),
      mapCellProps: (d: AuditLogRecord) => ({
        text: d.activity,
      }),
    },
    {
      key: 'details',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'AdminConsoleAuditLogs.detailsColumn',
        defaultMessage: 'Details',
        description: 'Column header for details',
      }),
      mapCellProps: (d: AuditLogRecord) => {
        return {
          text: d.details,
          limit: 60,
          shouldTruncate: true,
        };
      },
    },
    {
      key: 'initiatorId',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'AdminConsoleAuditLogs.initiatorIdColumn',
        defaultMessage: 'Initiator ID',
        description: 'Column header for initiator ID',
      }),
      mapCellProps: (d: AuditLogRecord) => ({
        text: d.initiatorId,
      }),
    },
    {
      key: 'initiatorType',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'AdminConsoleAuditLogs.initiatorTypeColumn',
        defaultMessage: 'Initiator Type',
        description: 'Column header for initiator type',
      }),
      mapCellProps: (d: AuditLogRecord) => ({
        text: d.initiatorType,
      }),
    },
    {
      key: 'recordId',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'AdminConsoleAuditLogs.recordIdColumn',
        defaultMessage: 'Record ID',
        description: 'Column header for record ID',
      }),
      mapCellProps: (d: AuditLogRecord) => ({
        text: d.recordId,
      }),
    },
  ];

  return (
    <PageLayout
      loadingContent={loadingContent}
      nav={nav}
      title={intl.formatMessage({
        id: 'AdminConsoleAuditLogs.pageTitle',
        defaultMessage: 'Audit Logs',
        description: 'Page title for audit logs',
      })}
    >
      <Layout preset="sections">
        <AuditLogsFilters
          filters={filters}
          onChange={(_updatedFilters: Filter[]) => {
            const updatedFilters = validateFilters(
              _updatedFilters,
              entityOptions,
            );

            setFilters(updatedFilters);
            setSearchParams({ filters: serializeFiltersParam(updatedFilters) });
          }}
        />
        {hasMore ? (
          <StatusMessage
            message={intl.formatMessage(
              {
                id: 'AdminConsoleAuditLogs.maxResultsMessage',
                defaultMessage:
                  'Maximum display limit reached. Only the first 50,000 records are shown here. To view more specific results, refine your search filters. For access to the complete dataset, please use the [Audit Logs API](https://auditlogs.developers.evisort.com/evisort-auditlogs-api).',
                description:
                  'Message when there are too many audit log results',
              },
              {
                link: 'https://auditlogs.developers.evisort.com/evisort-auditlogs-api',
                text: intl.formatMessage({
                  id: 'AdminConsoleAuditLogs.auditLogsApi',
                  defaultMessage: 'Audit Logs API',
                  description: 'Audit Logs API action text',
                }),
              },
            )}
          />
        ) : undefined}
        <ContentContainer placeholderContent={placeholderContent}>
          <PersistedTable
            actions={[
              {
                label: intl.formatMessage({
                  id: 'AdminConsoleAuditLogs.exportLabel',
                  defaultMessage: 'Export',
                  description: 'Export button label',
                }),
                icon: 'download',
                tooltip: intl.formatMessage({
                  id: 'AdminConsoleAuditLogs.exportTooltip',
                  defaultMessage: 'Export Excel',
                  description: 'Export button tooltip',
                }),
                onClick: (tableState: any) => {
                  const { columnOrder, name, totalCount } = tableState;
                  getAuditLogsExport({
                    filters,
                    sortBy,
                    pageSize: totalCount,
                    pageIndex: 1,
                    formatDate,
                  })
                    .then(async (response) => {
                      const prepareResponse = (
                        response.data?.results ?? []
                      ).map((record) => {
                        return columnOrder.reduce(
                          (newRecord: any, key: string) => {
                            if (key in record) {
                              newRecord[key] =
                                record[key as keyof AuditLogRecord];
                            }

                            return newRecord;
                          },
                          {},
                        );
                      });
                      await exportXlsx({
                        data: prepareResponse,
                        name,
                      });
                      toast({
                        status: 'success',
                        message: intl.formatMessage({
                          id: 'AdminConsoleAuditLogs.exportSuccess',
                          defaultMessage: 'Audit logs exported successfully.',
                          description:
                            'Success message after exporting audit logs',
                        }),
                      });
                    })
                    .catch(() => {
                      toast({
                        status: 'danger',
                        message: intl.formatMessage({
                          id: 'AdminConsoleAuditLogs.exportError',
                          defaultMessage: 'Failed to export audit logs.',
                          description: 'Error message when export fails',
                        }),
                      });
                    });
                },
              },
            ]}
            context={TableContextType.AuditLogs}
            name={intl.formatMessage({
              id: 'AdminConsoleAuditLogs.tableName',
              defaultMessage: 'Audit Logs',
              description: 'Table name for audit logs',
            })}
            data={results}
            columns={columns}
            options={tableOptions}
            state={{
              pageIndex,
              pageSize,
              sortBy: [sortBy],
            }}
            totalCount={totalCount}
            reactTableOptions={{
              manualSortBy: true,
            }}
            onPaginate={({ pageIndex }) => {
              setSearchParams({
                pageIndex: String(pageIndex),
              });
            }}
            onPageSizeChange={(pageSize) => {
              setSearchParams({
                pageIndex: '1',
                pageSize: String(pageSize),
              });
            }}
            onUpdate={(state, action) => {
              if (action?.type === 'toggleSortBy') {
                setSearchParams({
                  pageIndex: '1', // reset page index when sorting
                  sortBy: serializeSortByParam({
                    id: action.columnId,
                    desc: state.sortBy[0]?.desc,
                  }),
                });
              }
            }}
          />
        </ContentContainer>
      </Layout>
    </PageLayout>
  );
};

const initialTableSettings = {
  pageIndex: 1,
  pageSize: 1000,
  columnOrder: [
    'datetime',
    'initiatorName',
    'entityType',
    'entityName',
    'activity',
    'details',
  ],
  sortBy: {
    id: 'datetime',
    desc: true,
  },
};

const tableOptions = {
  enableExportXlsx: false,
  enablePagination: true,
  enableManageColumns: true,
  enableSelectRows: false,
  pageSizes: [100, 250, 500, 750, 1000],
};

const serializeFiltersParam = (filters: Filter[]) => {
  const filtersWithDatesConvertedToStrings = filters.map((filter) => {
    if (filter.fieldType === 'date') {
      return {
        ...filter,
        values: filter.values.map((value) =>
          value instanceof Date ? value.toISOString() : value,
        ),
      };
    }
    return filter;
  });
  return btoa(JSON.stringify(filtersWithDatesConvertedToStrings));
};

const parseFiltersParams = (filtersParam: string) => {
  return JSON.parse(atob(filtersParam)).map((filter: Filter) => {
    if (filter.fieldType === 'date') {
      return {
        ...filter,
        values: filter.values.map(
          (value) => new Date(value as string | number),
        ),
      };
    }
    return filter;
  });
};

const parseSortByParam = (sortByParam = '') => {
  const [sortColumn, sortOrder] = sortByParam.split(':') as [
    keyof AuditLogRecord,
    'asc' | 'desc',
  ];
  return sortColumn && sortOrder
    ? {
        id: sortColumn,
        desc: sortOrder === 'desc',
      }
    : undefined;
};

const serializeSortByParam = ({ id, desc }: { id: string; desc: boolean }) =>
  `${id}:${desc ? 'desc' : 'asc'}`;

export default Page;
